import { useEffect } from "react";
import api from "@/lib/api";

// In-memory cache so we don't refetch on every page change
let _cache = null;
let _fetchPromise = null;

const fetchSettings = () => {
    if (_cache) return Promise.resolve(_cache);
    if (_fetchPromise) return _fetchPromise;
    _fetchPromise = api.get("/settings").then((r) => {
        _cache = r.data || {};
        return _cache;
    }).catch(() => ({})).finally(() => { _fetchPromise = null; });
    return _fetchPromise;
};

const upsertMeta = (selector, attrs) => {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        document.head.appendChild(el);
    }
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

const upsertLink = (rel, href) => {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
};

/**
 * Apply SEO meta tags for a page.
 * @param {object} opts
 * @param {string} opts.title — page-specific title (e.g. "Komunikat: X"). Will be merged with template.
 * @param {string} opts.description — page description (max ~160 chars)
 * @param {string} opts.image — optional OG image URL
 * @param {string} opts.url — canonical URL (defaults to current location)
 * @param {string} opts.type — og:type (default: website)
 */
export default function useSeo({ title, description, image, url, type = "website" } = {}) {
    useEffect(() => {
        let cancelled = false;
        fetchSettings().then((s) => {
            if (cancelled) return;
            const siteName = s.seo_site_name || "Dąbrówka Wiślana";
            const tpl = s.seo_title_template || "%s · Dąbrówka Wiślana";
            const finalTitle = title
                ? tpl.includes("%s") ? tpl.replace("%s", title) : `${title} – ${siteName}`
                : (s.seo_default_title || siteName);
            const finalDesc = (description || s.seo_default_description || "").slice(0, 300);
            const finalImage = image || s.seo_og_image_url || "";
            const finalUrl = url || (typeof window !== "undefined" ? window.location.href : "");
            const locale = s.seo_locale || "pl_PL";
            const robots = s.seo_robots || "index,follow";
            const keywords = s.seo_keywords || "";
            const twitter = s.seo_twitter_handle || "";
            const fav = s.seo_favicon_url || "";

            document.title = finalTitle;
            upsertMeta('meta[name="description"]', { name: "description", content: finalDesc });
            upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
            if (keywords) upsertMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
            // OG
            upsertMeta('meta[property="og:title"]', { property: "og:title", content: finalTitle });
            upsertMeta('meta[property="og:description"]', { property: "og:description", content: finalDesc });
            upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
            upsertMeta('meta[property="og:url"]', { property: "og:url", content: finalUrl });
            upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: locale });
            upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: siteName });
            if (finalImage) upsertMeta('meta[property="og:image"]', { property: "og:image", content: finalImage });
            // Twitter
            upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: finalImage ? "summary_large_image" : "summary" });
            upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: finalTitle });
            upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: finalDesc });
            if (finalImage) upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: finalImage });
            if (twitter) upsertMeta('meta[name="twitter:site"]', { name: "twitter:site", content: twitter });
            // Canonical
            upsertLink("canonical", finalUrl);
            if (fav) upsertLink("icon", fav);
        });
        return () => { cancelled = true; };
    }, [title, description, image, url, type]);
}

export const invalidateSeoCache = () => { _cache = null; };
